<script setup>
import { AppIcon } from '@/components/shared'

import Heading from './Heading.vue'

/**
 * Empty state - the "there's nothing here yet" panel that appears when a list /
 * grid / collection comes back empty. The audit found 6 places hand-rolling
 * this with subtly different `rounded-{md|lg|xl}`, icon size 4xl/5xl/6xl,
 * padding p-8/p-10/p-12/p-16, and heading sizes xs/sm/none. This component
 * locks a single rhythm so the rider/photographer sees the same empty-state
 * shape whether they hit "Žádné objednávky" or "Zatím žádné galerie".
 *
 * Anatomy (top → bottom):
 *   - Material Symbols icon - zinc-700, centered
 *   - Heading (size based on `density` prop)
 *   - Description paragraph - text-on-surface-variant
 *   - Optional CTA slot - typically a `<PrimaryButton>`
 *
 * Two densities so a tight inline empty state (inside a card) and a hero
 * empty state (full-page panel) share the same component:
 *   - `compact` (default) → tighter padding, smaller icon. Use inside cards.
 *   - `hero`              → big padding, bigger icon. Use as the full-page
 *                           main content when the page is otherwise empty.
 */
defineProps({
    /** Material Symbols glyph name (e.g. `photo_library`, `shopping_cart`). */
    icon: {
        type: String,
        required: true,
    },
    /** Heading text - the headline that names the empty state. */
    title: {
        type: String,
        required: true,
    },
    /** Optional description shown under the title. */
    description: {
        type: String,
        default: '',
    },
    /**
     * `compact` (default) - used inside section cards, lists, grids. Tighter
     * padding (p-12), smaller icon (5xl).
     * `hero` - used as full-page empty state. More padding (p-16) + larger
     * icon (6xl) so it carries the page's visual weight on its own.
     */
    density: {
        type: String,
        default: 'compact',
        validator: (v) => ['compact', 'hero'].includes(v),
    },
})
</script>

<template>
    <div
        class="bg-surface-container-low rounded-xl border border-dashed border-zinc-800 text-center"
        :class="density === 'hero' ? 'p-16' : 'p-12'"
    >
        <AppIcon
            class="mb-4 block text-zinc-700"
            :name="icon"
            :class="density === 'hero' ? 'text-6xl' : 'text-5xl'"
        />
        <Heading
            class="mb-2"
            as="h2"
            :size="density === 'hero' ? 'sm' : 'xs'"
            weight="bold"
            align="center"
            :text="title"
        />
        <p
            class="text-on-surface-variant mx-auto max-w-md text-sm leading-relaxed"
            v-if="description"
        >
            {{ description }}
        </p>
        <!-- CTA slot - render whatever action the empty state needs. Caller
             usually wraps a <Link> around a <PrimaryButton>. Margin matches
             the rhythm of the Heading + description above. -->
        <div
            class="mt-6 flex justify-center"
            v-if="$slots.cta"
        >
            <slot name="cta" />
        </div>
    </div>
</template>
